/**
 * The theme builder's BACKGROUND option: the tones the picker offers for the page and for the sidebar.
 * Pure data and lookups — no DOM, no storage (components/theme-switcher.tsx keeps the choice;
 * components/app-theme/builder-backgrounds.css paints it).
 *
 * Owner, 17 Oct 2026: "give an option in a theme itself to select the background ... all palettes and
 * variation on selection ... both the sidebar background and the main content background", and "an option
 * to select the colors background and sidebar color". Then, of the yellow that had been the page: "too
 * prominent - flat white, a minor change only" — so "Default" (what the app paints with no choice) is flat
 * white with a minor warm tint, and every family starts close to white and gets stronger.
 *
 *   - The PAGE is the surface behind the cards (--canvas). Nine light families, each a few variations from
 *     the most subtle to the strongest. White is pure #ffffff and has one.
 *   - The SIDEBAR has the same nine light families (it also needs its active item, --sidebar-accent, a step
 *     darker, and its hairline) and five real colours after them — Charcoal, Navy, Slate, Forest and Plum —
 *     which are dark: in light mode they re-scope the whole sidebar on the sidebar alone (the island), the
 *     way Sentinel's rail does, so its words, chip, ring and the brand mark read on them.
 *   - Light mode only. Dark mode keeps its own tokens; a dark sidebar tone shows in light mode.
 *
 * A tone is in this catalogue only if it is valid: lib/theme/app-themes/__tests__/app-backgrounds.spec.ts
 * measures every value from the CSS actually written (contrast of the text on it, the step bars and tab
 * lists on it, the active item on it) and fails on an orphan either way. Add a tone here AND in the sheet.
 */

/** The picker's "Default": no override. The page keeps globals.css's --canvas, the sidebar its --sidebar. */
export const BG_DEFAULT = 'default'

export const PAGE_FAMILY_IDS = ['white', 'yellow', 'ivory', 'linen', 'cool', 'grey', 'mint', 'lavender', 'blush'] as const
export const SIDEBAR_DARK_FAMILY_IDS = ['charcoal', 'navy', 'slate', 'forest', 'plum'] as const
/** The sidebar offers the light families, then the dark ones. */
export const SIDEBAR_FAMILY_IDS = [...PAGE_FAMILY_IDS, ...SIDEBAR_DARK_FAMILY_IDS] as const

export type PageFamilyId = (typeof PAGE_FAMILY_IDS)[number]
export type SidebarDarkFamilyId = (typeof SIDEBAR_DARK_FAMILY_IDS)[number]
export type BgFamilyId = PageFamilyId | SidebarDarkFamilyId

/** Put on <body> with a dark sidebar tone: the switch for the dark island and the dark-surface brand mark. */
export const SIDEBAR_DARK_CLASS = 'sidebar-dark'

/** What the page shows with no choice (globals.css :root --canvas): flat white, a minor warm tint (owner, 17 Oct). */
export const PAGE_DEFAULT_CANVAS = 'oklch(0.995 0.006 95)'
/** What the sidebar shows with no choice (globals.css :root --sidebar, --sidebar-accent, --sidebar-border). */
export const SIDEBAR_DEFAULT = { sidebar: 'oklch(0.985 0 0)', accent: 'oklch(0.97 0 0)', border: 'oklch(0.922 0 0)' } as const

interface ToneBase {
  /** `<family>-<n>`, n from 1 (the most subtle) — also the class suffix and what is kept in localStorage. */
  id: string
  /** Which variation of its family (1-based), and how many the family has: "Light yellow, 2 of 4". */
  index: number
  of: number
}

export interface PageTone extends ToneBase {
  family: PageFamilyId
  /** --canvas */
  canvas: string
}

interface SidebarToneBase extends ToneBase {
  /** --sidebar */
  sidebar: string
  /** --sidebar-accent: the active and hovered item. */
  accent: string
  /** --sidebar-border */
  border: string
}

export interface LightSidebarTone extends SidebarToneBase {
  family: PageFamilyId
  dark: false
}

/** A real colour: also brings the words, chip, ring and secondary text the dark surface needs (the island). */
export interface DarkSidebarTone extends SidebarToneBase {
  family: SidebarDarkFamilyId
  dark: true
  /** --sidebar-foreground */
  foreground: string
  /** --sidebar-accent-foreground */
  accentForeground: string
  /** --muted-foreground, inside the sidebar */
  mutedForeground: string
  /** --sidebar-primary: the workspace chip. */
  primary: string
  /** --sidebar-primary-foreground */
  primaryForeground: string
  /** --sidebar-ring */
  ring: string
}

export type SidebarTone = LightSidebarTone | DarkSidebarTone

interface DarkValues {
  sidebar: string
  accent: string
  border: string
  foreground: string
  accentForeground: string
  mutedForeground: string
  primary: string
  primaryForeground: string
  ring: string
}

const PAGE_CANVASES: Record<PageFamilyId, readonly string[]> = {
  white: ['#ffffff'],
  yellow: ['oklch(0.996 0.009 100)', 'oklch(0.993 0.015 100)', 'oklch(0.988 0.024 100)', 'oklch(0.98 0.038 100)'],
  ivory: ['oklch(0.992 0.006 78)', 'oklch(0.982 0.012 78)', 'oklch(0.97 0.021 78)'],
  linen: ['oklch(0.992 0.004 55)', 'oklch(0.982 0.008 55)', 'oklch(0.97 0.014 55)'],
  cool: ['oklch(0.992 0.003 250)', 'oklch(0.982 0.007 250)', 'oklch(0.97 0.012 250)'],
  grey: ['oklch(0.992 0 0)', 'oklch(0.982 0 0)', 'oklch(0.97 0 0)'],
  mint: ['oklch(0.992 0.009 165)', 'oklch(0.982 0.018 165)', 'oklch(0.97 0.03 165)'],
  lavender: ['oklch(0.992 0.003 295)', 'oklch(0.982 0.007 295)', 'oklch(0.97 0.012 295)'],
  blush: ['oklch(0.992 0.003 10)', 'oklch(0.982 0.007 10)', 'oklch(0.97 0.012 10)'],
}

/** [sidebar, active item (--sidebar-accent), hairline (--sidebar-border)] */
const LIGHT_SIDEBARS: Record<PageFamilyId, ReadonlyArray<readonly [string, string, string]>> = {
  white: [
    ['#ffffff', 'oklch(0.962 0 0)', 'oklch(0.922 0 0)'],
  ],
  yellow: [
    ['oklch(0.992 0.01 100)', 'oklch(0.957 0.017 100)', 'oklch(0.922 0.016 100)'],
    ['oklch(0.986 0.018 100)', 'oklch(0.951 0.029 100)', 'oklch(0.916 0.027 100)'],
    ['oklch(0.978 0.028 100)', 'oklch(0.943 0.045 100)', 'oklch(0.908 0.041 100)'],
    ['oklch(0.968 0.04 100)', 'oklch(0.933 0.065 100)', 'oklch(0.898 0.058 100)'],
  ],
  ivory: [
    ['oklch(0.99 0.006 78)', 'oklch(0.955 0.01 78)', 'oklch(0.92 0.01 78)'],
    ['oklch(0.98 0.012 78)', 'oklch(0.945 0.02 78)', 'oklch(0.91 0.019 78)'],
    ['oklch(0.968 0.021 78)', 'oklch(0.933 0.034 78)', 'oklch(0.898 0.031 78)'],
  ],
  linen: [
    ['oklch(0.99 0.004 55)', 'oklch(0.955 0.007 55)', 'oklch(0.92 0.008 55)'],
    ['oklch(0.98 0.008 55)', 'oklch(0.945 0.013 55)', 'oklch(0.91 0.013 55)'],
    ['oklch(0.968 0.014 55)', 'oklch(0.933 0.023 55)', 'oklch(0.898 0.022 55)'],
  ],
  cool: [
    ['oklch(0.99 0.003 250)', 'oklch(0.955 0.005 250)', 'oklch(0.92 0.006 250)'],
    ['oklch(0.98 0.007 250)', 'oklch(0.945 0.012 250)', 'oklch(0.91 0.012 250)'],
    ['oklch(0.968 0.012 250)', 'oklch(0.933 0.02 250)', 'oklch(0.898 0.019 250)'],
  ],
  grey: [
    ['oklch(0.99 0 0)', 'oklch(0.955 0 0)', 'oklch(0.92 0 0)'],
    ['oklch(0.98 0 0)', 'oklch(0.945 0 0)', 'oklch(0.91 0 0)'],
    ['oklch(0.968 0 0)', 'oklch(0.933 0 0)', 'oklch(0.898 0 0)'],
  ],
  mint: [
    ['oklch(0.99 0.009 165)', 'oklch(0.955 0.015 165)', 'oklch(0.92 0.015 165)'],
    ['oklch(0.98 0.018 165)', 'oklch(0.945 0.029 165)', 'oklch(0.91 0.027 165)'],
    ['oklch(0.968 0.03 165)', 'oklch(0.933 0.049 165)', 'oklch(0.898 0.044 165)'],
  ],
  lavender: [
    ['oklch(0.99 0.003 295)', 'oklch(0.955 0.005 295)', 'oklch(0.92 0.006 295)'],
    ['oklch(0.98 0.007 295)', 'oklch(0.945 0.012 295)', 'oklch(0.91 0.012 295)'],
    ['oklch(0.968 0.012 295)', 'oklch(0.933 0.02 295)', 'oklch(0.898 0.019 295)'],
  ],
  blush: [
    ['oklch(0.99 0.003 10)', 'oklch(0.955 0.005 10)', 'oklch(0.92 0.006 10)'],
    ['oklch(0.98 0.007 10)', 'oklch(0.945 0.012 10)', 'oklch(0.91 0.012 10)'],
    ['oklch(0.968 0.012 10)', 'oklch(0.933 0.02 10)', 'oklch(0.898 0.019 10)'],
  ],
}

const DARK_SIDEBARS: Record<SidebarDarkFamilyId, ReadonlyArray<DarkValues>> = {
  charcoal: [
    {
      sidebar: 'oklch(0.31 0.006 260)',
      accent: 'oklch(0.385 0.006 260)',
      border: 'oklch(0.36 0.006 260)',
      foreground: 'oklch(0.985 0.004 260)',
      accentForeground: 'oklch(0.99 0.003 260)',
      mutedForeground: 'oklch(0.84 0.02 260)',
      primary: 'oklch(0.8 0.02 260)',
      primaryForeground: 'oklch(0.2 0.01 260)',
      ring: 'oklch(0.78 0.018 260)',
    },
    {
      sidebar: 'oklch(0.25 0.005 260)',
      accent: 'oklch(0.325 0.005 260)',
      border: 'oklch(0.3 0.005 260)',
      foreground: 'oklch(0.985 0.004 260)',
      accentForeground: 'oklch(0.99 0.003 260)',
      mutedForeground: 'oklch(0.84 0.02 260)',
      primary: 'oklch(0.8 0.02 260)',
      primaryForeground: 'oklch(0.2 0.01 260)',
      ring: 'oklch(0.78 0.018 260)',
    },
    {
      sidebar: 'oklch(0.19 0.004 260)',
      accent: 'oklch(0.265 0.004 260)',
      border: 'oklch(0.24 0.004 260)',
      foreground: 'oklch(0.985 0.004 260)',
      accentForeground: 'oklch(0.99 0.003 260)',
      mutedForeground: 'oklch(0.84 0.02 260)',
      primary: 'oklch(0.8 0.02 260)',
      primaryForeground: 'oklch(0.2 0.01 260)',
      ring: 'oklch(0.78 0.018 260)',
    },
  ],
  navy: [
    {
      sidebar: 'oklch(0.31 0.055 255)',
      accent: 'oklch(0.385 0.058 255)',
      border: 'oklch(0.36 0.055 255)',
      foreground: 'oklch(0.985 0.004 255)',
      accentForeground: 'oklch(0.99 0.003 255)',
      mutedForeground: 'oklch(0.84 0.02 255)',
      primary: 'oklch(0.8 0.094 255)',
      primaryForeground: 'oklch(0.2 0.03 255)',
      ring: 'oklch(0.78 0.09 255)',
    },
    {
      sidebar: 'oklch(0.25 0.052 255)',
      accent: 'oklch(0.325 0.055 255)',
      border: 'oklch(0.3 0.052 255)',
      foreground: 'oklch(0.985 0.004 255)',
      accentForeground: 'oklch(0.99 0.003 255)',
      mutedForeground: 'oklch(0.84 0.02 255)',
      primary: 'oklch(0.8 0.094 255)',
      primaryForeground: 'oklch(0.2 0.03 255)',
      ring: 'oklch(0.78 0.09 255)',
    },
    {
      sidebar: 'oklch(0.19 0.048 255)',
      accent: 'oklch(0.265 0.05 255)',
      border: 'oklch(0.24 0.048 255)',
      foreground: 'oklch(0.985 0.004 255)',
      accentForeground: 'oklch(0.99 0.003 255)',
      mutedForeground: 'oklch(0.84 0.02 255)',
      primary: 'oklch(0.8 0.094 255)',
      primaryForeground: 'oklch(0.2 0.03 255)',
      ring: 'oklch(0.78 0.09 255)',
    },
  ],
  slate: [
    {
      sidebar: 'oklch(0.31 0.028 245)',
      accent: 'oklch(0.385 0.029 245)',
      border: 'oklch(0.36 0.028 245)',
      foreground: 'oklch(0.985 0.004 245)',
      accentForeground: 'oklch(0.99 0.003 245)',
      mutedForeground: 'oklch(0.84 0.02 245)',
      primary: 'oklch(0.8 0.05 245)',
      primaryForeground: 'oklch(0.2 0.03 245)',
      ring: 'oklch(0.78 0.045 245)',
    },
    {
      sidebar: 'oklch(0.25 0.026 245)',
      accent: 'oklch(0.325 0.027 245)',
      border: 'oklch(0.3 0.026 245)',
      foreground: 'oklch(0.985 0.004 245)',
      accentForeground: 'oklch(0.99 0.003 245)',
      mutedForeground: 'oklch(0.84 0.02 245)',
      primary: 'oklch(0.8 0.05 245)',
      primaryForeground: 'oklch(0.2 0.03 245)',
      ring: 'oklch(0.78 0.045 245)',
    },
    {
      sidebar: 'oklch(0.19 0.022 245)',
      accent: 'oklch(0.265 0.023 245)',
      border: 'oklch(0.24 0.022 245)',
      foreground: 'oklch(0.985 0.004 245)',
      accentForeground: 'oklch(0.99 0.003 245)',
      mutedForeground: 'oklch(0.84 0.02 245)',
      primary: 'oklch(0.8 0.05 245)',
      primaryForeground: 'oklch(0.2 0.03 245)',
      ring: 'oklch(0.78 0.045 245)',
    },
  ],
  forest: [
    {
      sidebar: 'oklch(0.31 0.05 155)',
      accent: 'oklch(0.385 0.053 155)',
      border: 'oklch(0.36 0.05 155)',
      foreground: 'oklch(0.985 0.004 155)',
      accentForeground: 'oklch(0.99 0.003 155)',
      mutedForeground: 'oklch(0.84 0.02 155)',
      primary: 'oklch(0.8 0.11 155)',
      primaryForeground: 'oklch(0.2 0.03 155)',
      ring: 'oklch(0.78 0.099 155)',
    },
    {
      sidebar: 'oklch(0.25 0.046 155)',
      accent: 'oklch(0.325 0.048 155)',
      border: 'oklch(0.3 0.046 155)',
      foreground: 'oklch(0.985 0.004 155)',
      accentForeground: 'oklch(0.99 0.003 155)',
      mutedForeground: 'oklch(0.84 0.02 155)',
      primary: 'oklch(0.8 0.11 155)',
      primaryForeground: 'oklch(0.2 0.03 155)',
      ring: 'oklch(0.78 0.099 155)',
    },
    {
      sidebar: 'oklch(0.19 0.04 155)',
      accent: 'oklch(0.265 0.042 155)',
      border: 'oklch(0.24 0.04 155)',
      foreground: 'oklch(0.985 0.004 155)',
      accentForeground: 'oklch(0.99 0.003 155)',
      mutedForeground: 'oklch(0.84 0.02 155)',
      primary: 'oklch(0.8 0.11 155)',
      primaryForeground: 'oklch(0.2 0.03 155)',
      ring: 'oklch(0.78 0.099 155)',
    },
  ],
  plum: [
    {
      sidebar: 'oklch(0.31 0.06 335)',
      accent: 'oklch(0.385 0.063 335)',
      border: 'oklch(0.36 0.06 335)',
      foreground: 'oklch(0.985 0.004 335)',
      accentForeground: 'oklch(0.99 0.003 335)',
      mutedForeground: 'oklch(0.84 0.02 335)',
      primary: 'oklch(0.8 0.1 335)',
      primaryForeground: 'oklch(0.2 0.03 335)',
      ring: 'oklch(0.78 0.09 335)',
    },
    {
      sidebar: 'oklch(0.25 0.056 335)',
      accent: 'oklch(0.325 0.059 335)',
      border: 'oklch(0.3 0.056 335)',
      foreground: 'oklch(0.985 0.004 335)',
      accentForeground: 'oklch(0.99 0.003 335)',
      mutedForeground: 'oklch(0.84 0.02 335)',
      primary: 'oklch(0.8 0.1 335)',
      primaryForeground: 'oklch(0.2 0.03 335)',
      ring: 'oklch(0.78 0.09 335)',
    },
    {
      sidebar: 'oklch(0.19 0.05 335)',
      accent: 'oklch(0.265 0.053 335)',
      border: 'oklch(0.24 0.05 335)',
      foreground: 'oklch(0.985 0.004 335)',
      accentForeground: 'oklch(0.99 0.003 335)',
      mutedForeground: 'oklch(0.84 0.02 335)',
      primary: 'oklch(0.8 0.1 335)',
      primaryForeground: 'oklch(0.2 0.03 335)',
      ring: 'oklch(0.78 0.09 335)',
    },
  ],
}

const toneId = (family: string, n: number) => `${family}-${n}`

export const PAGE_TONES: readonly PageTone[] = PAGE_FAMILY_IDS.flatMap((family) => {
  const canvases = PAGE_CANVASES[family]
  return canvases.map((canvas, i): PageTone => ({ id: toneId(family, i + 1), family, index: i + 1, of: canvases.length, canvas }))
})

export const SIDEBAR_TONES: readonly SidebarTone[] = [
  ...PAGE_FAMILY_IDS.flatMap((family) => {
    const tones = LIGHT_SIDEBARS[family]
    return tones.map(([sidebar, accent, border], i): SidebarTone => ({ id: toneId(family, i + 1), family, index: i + 1, of: tones.length, dark: false, sidebar, accent, border }))
  }),
  ...SIDEBAR_DARK_FAMILY_IDS.flatMap((family) => {
    const tones = DARK_SIDEBARS[family]
    return tones.map((values, i): SidebarTone => ({ id: toneId(family, i + 1), family, index: i + 1, of: tones.length, dark: true, ...values }))
  }),
]

export const pageBgClass = (id: string) => `page-bg-${id}`
export const sidebarBgClass = (id: string) => `sidebar-bg-${id}`

/** Every class a page / sidebar tone can put on <body>: the switcher removes them all before it adds the chosen one. */
export const PAGE_BG_CLASSES: readonly string[] = PAGE_TONES.map((t) => pageBgClass(t.id))
export const SIDEBAR_BG_CLASSES: readonly string[] = SIDEBAR_TONES.map((t) => sidebarBgClass(t.id))

// A Map, not an object: a saved value of "__proto__" or "constructor" must be a stranger, not a hit.
const PAGE_BY_ID = new Map<string, PageTone>(PAGE_TONES.map((t) => [t.id, t]))
const SIDEBAR_BY_ID = new Map<string, SidebarTone>(SIDEBAR_TONES.map((t) => [t.id, t]))

/** The page tone with this id, or undefined (`default`, a value the catalogue no longer holds, nothing at all). */
export const pageToneById = (id: string | null | undefined): PageTone | undefined => (typeof id === 'string' ? PAGE_BY_ID.get(id) : undefined)
export const sidebarToneById = (id: string | null | undefined): SidebarTone | undefined => (typeof id === 'string' ? SIDEBAR_BY_ID.get(id) : undefined)
export const isPageToneId = (id: string | null | undefined): boolean => pageToneById(id) !== undefined
export const isSidebarToneId = (id: string | null | undefined): boolean => sidebarToneById(id) !== undefined

/** Tones grouped by family, in the order the families first appear: what the picker draws, a cluster each. */
export function tonesByFamily<T extends { family: string }>(tones: readonly T[]): Array<{ family: T['family']; tones: T[] }> {
  const groups: Array<{ family: T['family']; tones: T[] }> = []
  for (const tone of tones) {
    const group = groups.find((g) => g.family === tone.family)
    if (group) group.tones.push(tone)
    else groups.push({ family: tone.family, tones: [tone] })
  }
  return groups
}
